import { requiresEnhancedIndividualKyc } from './individualEnhancedKyc';

export const USER_ONBOARDING_PROFILE_FIELDS = [
  'otherCitizenships',
  'accountUse',
  'paymentTypes',
  'paymentTypesOther',
  'monthlyPaymentVolume',
  'destinationCountries',
  'sourceIncome',
  'sourceIncomeOther',
  'employmentStatus',
  'employmentStatusOther',
  'employmentIndustry',
  'employmentIndustryOther',
  'mostRecentOccupation',
  'actingAsIntermediary',
  'estimatedIncome',
  'isSanctioned',
  'sanctionsDetail',
  'hasCriminalConvictions',
  'criminalConvictionsDetail',
  'isPoliticallyExposed',
];

export function normalizeMultiValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function getUserOnboardingProfile(user) {
  return user?.userDetails?.onboardingProfile ?? user?.userDetails?.OnboardingProfile ?? {};
}

function getProfileValue(profile, camelCaseField, pascalCaseField) {
  return profile?.[camelCaseField] ?? profile?.[pascalCaseField];
}

function getPepAssociationFromProfile(profile) {
  const pepAssociation = profile?.pepAssociation ?? profile?.PepAssociation;
  if (pepAssociation) return pepAssociation;

  const role = getProfileValue(profile, 'pepRole', 'PepRole');
  const institutionOrOrganisation = getProfileValue(profile, 'pepInstitutionOrOrganisation', 'PepInstitutionOrOrganisation');
  const country = getProfileValue(profile, 'pepCountry', 'PepCountry');
  const datesHeld = getProfileValue(profile, 'pepDates', 'PepDates');

  if (role || institutionOrOrganisation || country || datesHeld) {
    return {
      role: role ?? null,
      institutionOrOrganisation: institutionOrOrganisation ?? null,
      country: country ?? null,
      datesHeld: datesHeld ?? null,
    };
  }

  return null;
}

function normalizeProfileInput(profile) {
  return {
    ...profile,
    isSanctioned: profile.isSanctioned ?? profile.IsSanctioned ?? profile.sanctioned ?? profile.Sanctioned ?? null,
    hasCriminalConvictions: profile.hasCriminalConvictions ?? profile.HasCriminalConvictions ?? profile.criminalConvictions ?? profile.CriminalConvictions ?? null,
    criminalConvictionsDetail: profile.criminalConvictionsDetail ?? profile.CriminalConvictionsDetail ?? profile.criminalDetail ?? profile.CriminalDetail ?? null,
    isPoliticallyExposed: profile.isPoliticallyExposed ?? profile.IsPoliticallyExposed ?? profile.politicallyExposed ?? profile.PoliticallyExposed ?? null,
    mostRecentOccupation: profile.mostRecentOccupation ?? profile.MostRecentOccupation ?? null,
    actingAsIntermediary: profile.actingAsIntermediary ?? profile.ActingAsIntermediary ?? null,
    pepAssociation: profile.pepAssociation !== undefined ? profile.pepAssociation : getPepAssociationFromProfile(profile),
  };
}

export function buildUserOnboardingProfilePayload(profile) {
  const normalizedProfile = normalizeProfileInput(profile);
  const payload = USER_ONBOARDING_PROFILE_FIELDS.reduce(
    (payload, field) => ({
      ...payload,
      [field]: normalizedProfile[field] ?? null,
    }),
    {},
  );

  payload.pepAssociation = normalizedProfile.pepAssociation ?? null;

  return payload;
}

function hasBooleanValue(value) {
  return typeof value === 'boolean';
}

function hasTextValue(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isFinancialFirstScreenComplete(user) {
  const profile = getUserOnboardingProfile(user);

  return Boolean(
    getProfileValue(profile, 'financialFirstScreenCompleted', 'FinancialFirstScreenCompleted') ||
      (getProfileValue(profile, 'accountUse', 'AccountUse') &&
        normalizeMultiValue(getProfileValue(profile, 'paymentTypes', 'PaymentTypes')).length > 0 &&
        getProfileValue(profile, 'monthlyPaymentVolume', 'MonthlyPaymentVolume') &&
        normalizeMultiValue(getProfileValue(profile, 'destinationCountries', 'DestinationCountries')).length > 0),
  );
}

export function isFinancialSecondScreenComplete(user) {
  const profile = getUserOnboardingProfile(user);
  const sourceIncome = getProfileValue(profile, 'sourceIncome', 'SourceIncome');
  const sourceIncomeOther = getProfileValue(profile, 'sourceIncomeOther', 'SourceIncomeOther');
  const employmentStatus = getProfileValue(profile, 'employmentStatus', 'EmploymentStatus');
  const employmentStatusOther = getProfileValue(profile, 'employmentStatusOther', 'EmploymentStatusOther');
  const employmentIndustry = getProfileValue(profile, 'employmentIndustry', 'EmploymentIndustry');
  const employmentIndustryOther = getProfileValue(profile, 'employmentIndustryOther', 'EmploymentIndustryOther');
  const mostRecentOccupation = getProfileValue(profile, 'mostRecentOccupation', 'MostRecentOccupation');
  const actingAsIntermediary = getProfileValue(profile, 'actingAsIntermediary', 'ActingAsIntermediary');
  const estimatedIncome = getProfileValue(profile, 'estimatedIncome', 'EstimatedIncome');
  const isEnhanced = requiresEnhancedIndividualKyc(user);

  const employmentOk =
    employmentStatus &&
    (employmentStatus !== 'Other' || hasTextValue(employmentStatusOther)) &&
    (isEnhanced
      ? hasTextValue(mostRecentOccupation) && hasBooleanValue(actingAsIntermediary)
      : !['Employed', 'SelfEmployed', 'BusinessOwner'].includes(employmentStatus) ||
        (employmentIndustry && (employmentIndustry !== 'Other' || hasTextValue(employmentIndustryOther))));

  return Boolean(
    getProfileValue(profile, 'financialSecondScreenCompleted', 'FinancialSecondScreenCompleted') ||
      (sourceIncome &&
        (sourceIncome !== 'Other' || hasTextValue(sourceIncomeOther)) &&
        employmentOk &&
        estimatedIncome),
  );
}

export function isFinancialOnboardingComplete(user) {
  return isFinancialFirstScreenComplete(user) && isFinancialSecondScreenComplete(user);
}

function isCompletionFlagTrue(value) {
  return value === true || value === 'true';
}

export function hasExplicitFinancialFirstScreenCompletion(user) {
  const profile = getUserOnboardingProfile(user);
  const first = getProfileValue(profile, 'financialFirstScreenCompleted', 'FinancialFirstScreenCompleted');
  return isCompletionFlagTrue(first);
}

export function hasExplicitFinancialSecondScreenCompletion(user) {
  const profile = getUserOnboardingProfile(user);
  const second = getProfileValue(profile, 'financialSecondScreenCompleted', 'FinancialSecondScreenCompleted');
  return isCompletionFlagTrue(second);
}

export function hasExplicitFinancialOnboardingCompletion(user) {
  return hasExplicitFinancialFirstScreenCompletion(user) && hasExplicitFinancialSecondScreenCompletion(user);
}

export function isComplianceOnboardingComplete(user) {
  const profile = normalizeProfileInput(getUserOnboardingProfile(user));
  const isPoliticallyExposed = getProfileValue(profile, 'isPoliticallyExposed', 'IsPoliticallyExposed');
  const pepRole = profile.pepAssociation?.role;
  const pepInstitutionOrOrganisation = profile.pepAssociation?.institutionOrOrganisation;
  const pepCountry = profile.pepAssociation?.country;
  const isSanctioned = getProfileValue(profile, 'isSanctioned', 'IsSanctioned');
  const sanctionsDetail = getProfileValue(profile, 'sanctionsDetail', 'SanctionsDetail');
  const hasCriminalConvictions = getProfileValue(profile, 'hasCriminalConvictions', 'HasCriminalConvictions');
  const criminalConvictionsDetail = getProfileValue(profile, 'criminalConvictionsDetail', 'CriminalConvictionsDetail');
  const isLowRisk = !requiresEnhancedIndividualKyc(user);

  return Boolean(
    hasBooleanValue(isPoliticallyExposed) &&
      (!isPoliticallyExposed || (hasTextValue(pepRole) && hasTextValue(pepInstitutionOrOrganisation) && hasTextValue(pepCountry))) &&
      hasBooleanValue(isSanctioned) &&
      (!isSanctioned || hasTextValue(sanctionsDetail)) &&
      (isLowRisk ||
        (hasBooleanValue(hasCriminalConvictions) && (!hasCriminalConvictions || hasTextValue(criminalConvictionsDetail)))),
  );
}
